import React, { useRef, useLayoutEffect, useState } from "react";
import PropTypes from 'prop-types';

const measureText = (text, fontSize = 12, fontWeight = 600, fontFamily = "Noto Sans, sans-serif") => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
};

const MIN_ARROW_WIDTH = 80;
const H_PADDING = 24;
const NOTCH = 10;

const computeRows = (stages, containerWidth) => {
    if (!containerWidth || !stages.length) return [];

    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;

    stages.forEach((stage, i) => {
        const labelWidth = Math.max(
            measureText(stage.option) + H_PADDING * 2 + NOTCH,
            MIN_ARROW_WIDTH
        );

        if (currentRowWidth + labelWidth > containerWidth + 1 && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }

        currentRow.push({
            ...stage,
            measuredWidth: labelWidth,
            originalIndex: i,
        });
        currentRowWidth += labelWidth;
    });

    if (currentRow.length) rows.push(currentRow);

    return rows.map((row, rowIndex) => {
        const isLastRow = rowIndex === rows.length - 1;
        const isSingleItem = row.length === 1;

        if (isLastRow && isSingleItem) {
            return row.map((s) => ({ ...s, arrowWidth: s.measuredWidth }));
        }

        const totalMeasured = row.reduce((sum, s) => sum + s.measuredWidth, 0);
        const scale = containerWidth / totalMeasured;
        return row.map((s) => ({ ...s, arrowWidth: s.measuredWidth * scale }));
    });
};

const ArrowPipeline = ({
    stages,
    currentStage,
    onStageClick,
    completedColor = "#0d6efd",
    upcomingColor = "#d3d3d3",
    arrowHeight = 50,
    disabledItem,
    displayData,
    pipeLineStage,
    oldPipeline,
    makeEdit,
    UpdatePs,
    canUpdateDelete,
}) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        });
        ro.observe(containerRef.current);
        setContainerWidth(containerRef.current.offsetWidth);
        return () => ro.disconnect();
    }, []);

    const rows = computeRows(stages, containerWidth);
    const totalStages = stages.length;

    const canEdit = canUpdateDelete(UpdatePs.permission, UpdatePs.dataPermission, UpdatePs.type, UpdatePs.PId);
    const isApprovedNone = displayData?.approved_for === "None";
    const isInteractive = canEdit && isApprovedNone && !makeEdit && !disabledItem;

    const rowGap = 8;
    const totalHeight = rows.length > 0
        ? rows.length * arrowHeight + (rows.length - 1) * rowGap
        : arrowHeight;

    const getReferenceIndex = () => {
        const referenceStage = oldPipeline !== null ? oldPipeline : currentStage;
        return pipeLineStage.findIndex(ps => ps.customDropdownOptionId === referenceStage);
    };

    return (
        <div ref={containerRef} style={{ width: "100%" }}>
            <svg
                height={totalHeight}
                width={containerWidth || "100%"}
                style={{ fontFamily: "'Noto Sans', sans-serif", display: "block" }}
            >
                {rows.map((row, rowIndex) => {
                    const rowY = rowIndex * (arrowHeight + rowGap);
                    let xCursor = 0;

                    return (
                        <g key={rowIndex} transform={`translate(0, ${rowY})`}>
                            {row.map((stage, i) => {
                                const { customDropdownOptionId, option, arrowWidth, originalIndex } = stage;
                                const isGlobalFirst = originalIndex === 0;
                                const isGlobalLast = originalIndex === totalStages - 1;
                                const isLastInRow = i === row.length - 1;
                                const x = xCursor;
                                xCursor += arrowWidth;
                                const flatLeft = isGlobalFirst;
                                const flatRight = isGlobalLast && isLastInRow;
                                const w = arrowWidth;

                                let points;
                                if (flatLeft && flatRight) {
                                    points = `${x},0 ${x + w},0 ${x + w},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatLeft) {
                                    points = `${x},0 ${x + w - NOTCH},0 ${x + w},${arrowHeight / 2} ${x + w - NOTCH},${arrowHeight} ${x},${arrowHeight}`;
                                } else if (flatRight) {
                                    points = `${x},0 ${x + w},0 ${x + w},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                } else {
                                    points = `${x},0 ${x + w - NOTCH},0 ${x + w},${arrowHeight / 2} ${x + w - NOTCH},${arrowHeight} ${x},${arrowHeight} ${x + NOTCH},${arrowHeight / 2}`;
                                }

                                const refIndex = getReferenceIndex();

                                const fillColor = (() => {
                                    if (!canEdit) return upcomingColor;
                                    if (!isApprovedNone) return upcomingColor;
                                    if (hoveredIndex !== null) {
                                        return originalIndex <= hoveredIndex ? completedColor : upcomingColor;
                                    }
                                    return originalIndex <= refIndex ? completedColor : upcomingColor;
                                })();

                                const textColor = (() => {
                                    if (!canEdit) return "#a1a1a1";
                                    if (!isApprovedNone) return "#a1a1a1";
                                    if (hoveredIndex !== null) {
                                        return originalIndex <= hoveredIndex ? "#ffffff" : "#a1a1a1";
                                    }
                                    return originalIndex <= refIndex ? "#ffffff" : "#a1a1a1";
                                })();

                                return (
                                    <g
                                        key={customDropdownOptionId}
                                        onClick={() => isInteractive && onStageClick(customDropdownOptionId)}
                                        onMouseEnter={() => isInteractive && setHoveredIndex(originalIndex)}
                                        onMouseLeave={() => isInteractive && setHoveredIndex(null)}
                                        style={{ cursor: isInteractive ? "pointer" : "default" }}
                                    >
                                        <polygon points={points} fill={fillColor} />
                                        <text
                                            fontFamily="'Noto Sans', sans-serif"
                                            x={x + arrowWidth / 2}
                                            y={arrowHeight / 2}
                                            textAnchor="middle"
                                            fill={textColor}
                                            fontSize="12"
                                            fontWeight="600"
                                            dominantBaseline="middle"
                                        >
                                            {option}
                                        </text>
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

ArrowPipeline.propTypes = {
    stages: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentStage: PropTypes.number.isRequired,
    onStageClick: PropTypes.func,
    completedColor: PropTypes.string,
    upcomingColor: PropTypes.string,
    arrowHeight: PropTypes.number,
    oldPipeline: PropTypes.number,
    disabledItem: PropTypes.bool,
    makeEdit: PropTypes.bool,
    displayData: PropTypes.object,
    pipeLineStage: PropTypes.array,
    UpdatePs: PropTypes.object.isRequired,
    canUpdateDelete: PropTypes.func.isRequired,
};

export default ArrowPipeline;